import React from 'react';
import { Controller, RegisterOptions } from 'react-hook-form';
import { Control } from 'react-hook-form/dist/types/form';
import { TextField, TextFieldProps } from '@mui/material';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import NumberFormat, { NumberFormatProps } from 'react-number-format';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';

export interface Props<FormValues extends FieldValues = FieldValues> extends NumberFormatProps {
  label: string;
  dataTestId?: string;
  range?: [number | undefined, number | undefined];
  control: Control<FormValues>;
  textFieldProps?: TextFieldProps;
  rules?: RegisterOptions<FormValues>;
  name: UseControllerProps<FormValues>['name'];
}

export const NumberFormatField = <FormValues extends FieldValues = FieldValues>({
  name,
  label,
  range,
  rules,
  control,
  dataTestId,
  textFieldProps = {},
  ...numberFormatProps
}: Props<FormValues>) => (
  <Controller
    name={name}
    rules={rules}
    control={control}
    render={({ field: { ref, value, onChange, onBlur, ...field }, fieldState }) => (
      <NumberFormat
        fullWidth
        label={label}
        inputRef={ref}
        variant="standard"
        customInput={TextField}
        helperText={fieldState.error ? fieldState.error.message : ''}
        {...field}
        {...(textFieldProps as any)}
        inputProps={{
          ...(textFieldProps as any)?.inputProps,
          ...(dataTestId ? { 'data-testid': dataTestId } : {}),
        }}
        error={!!textFieldProps?.error || !!fieldState.error}
        value={parseFloat(String(value ?? '').replace(/[^0-9.-]+/g, ''))}
        onValueChange={({ floatValue }) => {
          let newValue = floatValue;

          if (newValue === undefined) return onChange(null);

          if (range) {
            if (range[1] !== undefined && newValue > range[1]) newValue = range[1];
            if (range[0] !== undefined && newValue < range[0]) newValue = range[0];
          }

          onChange(newValue);
        }}
        onBlur={e => {
          onBlur();
          textFieldProps?.onBlur?.(e);
        }}
        {...(numberFormatProps ?? {})}
      />
    )}
  />
);
